import isNil from "lodash/isNil";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
	PaymentTypeLike,
} from "../types";
import { toRoundedNumber } from "./_internals";
import { calculateOrderGroup } from "./calculate-order-group";
import { WalletTransaction } from "~served/types/db";

export const isPaymentTypesValid = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	paymentTypes: PaymentTypeLike[],
	unpaidOrders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[],
	extraValidationFlags?: { is_payment_rounding_enabled?: boolean },
	walletTransactionInput?: Pick<WalletTransaction, "amount" | "payment_type">,
) => {
	if (paymentTypes.some((p) => !p.code || !p.payment_type || isNil(p.amount)))
		return false;

	if (walletTransactionInput && !walletTransactionInput.payment_type)
		return false;

	const { all } = calculateOrderGroup(unpaidOrders);
	const totalReceivedPayments = paymentTypes.reduce(
		(pre, cur) => bigMath.add(pre, cur.amount),
		walletTransactionInput?.amount ?? 0,
	);

	if (extraValidationFlags?.is_payment_rounding_enabled) {
		return toRoundedNumber(all.gross_amount) === totalReceivedPayments;
	}

	return all.gross_amount === totalReceivedPayments;
};

/**
 * paymentTypes should have the same currencies
 * this should be used after the payment is made
 * for before payment, use `isPaymentTypesValid`
 * */

export const isReceiptPaymentTypesValid = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	receipt: {
		payment_types: PaymentTypeLike[];
		rounding_difference_amount: number;
	},
	unpaidOrders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[],
) => {
	if (
		receipt.payment_types.some(
			(p) => !p.code || !p.payment_type || isNil(p.amount),
		)
	)
		return false;

	const { all } = calculateOrderGroup(unpaidOrders);
	const totalReceivedPayments = receipt.payment_types.reduce(
		(pre, cur) => bigMath.add(pre, cur.amount),
		0,
	);

	return (
		bigMath.add(all.gross_amount, receipt.rounding_difference_amount) ===
		totalReceivedPayments
	);
};

/**
 * Rounds a number (or string convertible to a number) to the nearest whole number if it's 0.5 or more.
 */

export const getRoundingDifference = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[],
) => {
	const { all } = calculateOrderGroup(orders);

	return bigMath.sub(toRoundedNumber(all.gross_amount), all.gross_amount);
};

export { toRoundedNumber } from "./_internals";
